import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Alert, CircularProgress } from '@mui/material';
import WorkflowBuilder from '../components/workflow/WorkflowBuilder';
import { WorkflowDefinition, CreateWorkflowRequest, UpdateWorkflowRequest } from '../types/workflows';

interface WorkflowApiService {
  getWorkflow: (id: string) => Promise<WorkflowDefinition>;
  createWorkflow: (request: CreateWorkflowRequest) => Promise<WorkflowDefinition>;
  updateWorkflow: (id: string, request: UpdateWorkflowRequest) => Promise<WorkflowDefinition>;
}

// Mock service for now - will be replaced with actual API calls
const mockWorkflowService: WorkflowApiService = {
  async getWorkflow(id: string): Promise<WorkflowDefinition> {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id,
          name: 'Sample Workflow',
          description: 'A sample workflow for testing',
          version: '1.0.0',
          category: 'Other',
          steps: [
            {
              id: 'start-1',
              name: 'Start',
              type: 'start',
              description: 'Workflow start point',
              position: { x: 100, y: 100 },
              configuration: {}
            },
            {
              id: 'task-1',
              name: 'Review Application',
              type: 'task',
              description: 'Manual review of the application',
              position: { x: 300, y: 100 },
              configuration: {}
            },
            {
              id: 'end-1',
              name: 'End',
              type: 'end',
              description: 'Workflow completion',
              position: { x: 500, y: 100 },
              configuration: {}
            }
          ],
          connections: [
            {
              id: 'conn-1',
              sourceStepId: 'start-1',
              sourceOutput: 'default',
              targetStepId: 'task-1',
              targetInput: 'default'
            },
            {
              id: 'conn-2',
              sourceStepId: 'task-1',
              sourceOutput: 'default',
              targetStepId: 'end-1',
              targetInput: 'default'
            }
          ],
          variables: [],
          triggers: [
            {
              id: 'trigger-1',
              type: 'manual',
              configuration: {}
            }
          ],
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'user-1'
        });
      }, 1000);
    });
  },

  async createWorkflow(request: CreateWorkflowRequest): Promise<WorkflowDefinition> {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id: `workflow-${Date.now()}`,
          ...request,
          version: '1.0.0',
          steps: request.steps || [],
          connections: request.connections || [],
          variables: request.variables || [],
          triggers: request.triggers || [],
          isActive: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'current-user'
        });
      }, 1000);
    });
  },

  async updateWorkflow(id: string, request: UpdateWorkflowRequest): Promise<WorkflowDefinition> {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id,
          name: request.name || 'Updated Workflow',
          description: request.description || '',
          category: request.category || 'Other',
          steps: request.steps || [],
          connections: request.connections || [],
          variables: request.variables || [],
          triggers: request.triggers || [],
          version: request.version || '1.0.1',
          isActive: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'current-user'
        });
      }, 1000);
    });
  }
};

const WorkflowBuilderPage: React.FC = () => {
  const { workflowId } = useParams<{ workflowId?: string }>();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState<WorkflowDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (workflowId && workflowId !== 'new') {
      loadWorkflow(workflowId);
    }
  }, [workflowId]);

  const loadWorkflow = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const workflowData = await mockWorkflowService.getWorkflow(id);
      setWorkflow(workflowData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (request: CreateWorkflowRequest | UpdateWorkflowRequest) => {
    try {
      setSaving(true);
      setError(null);

      let savedWorkflow: WorkflowDefinition;
      
      if (workflowId && workflowId !== 'new') {
        // Update existing workflow
        savedWorkflow = await mockWorkflowService.updateWorkflow(
          workflowId,
          request as UpdateWorkflowRequest
        );
      } else {
        // Create new workflow
        savedWorkflow = await mockWorkflowService.createWorkflow(
          request as CreateWorkflowRequest
        );
      }

      setWorkflow(savedWorkflow);
      
      // Show success message
      console.log('Workflow saved successfully:', savedWorkflow);
      // Navigate to the saved workflow if it was a new one
      if (workflowId === 'new') {
        navigate(`/workflows/builder/${savedWorkflow.id}`, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/workflows');
  };

  const handleTest = (testWorkflow: WorkflowDefinition) => {
    console.log('Testing workflow:', testWorkflow);
    // In a real implementation, this would trigger workflow validation and testing
    alert('Workflow test functionality will be implemented in a future version.');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {saving && (
        <Alert severity="info" sx={{ m: 0 }}>
          Saving workflow...
        </Alert>
      )}
      
      <WorkflowBuilder
        workflowId={workflowId === 'new' ? undefined : workflowId}
        initialWorkflow={workflow || undefined}
        onSave={handleSave}
        onCancel={handleCancel}
        onTest={handleTest}
      />
    </Box>
  );
};

export default WorkflowBuilderPage;